import { useMutation } from '@tanstack/react-query';
import { DocumentsService } from '@/api';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';

/**
 * Hook for the AUDITED document download (issue #1297).
 *
 * The `download_url` on list and detail responses is a convenience field minted
 * on every read, so it proves nothing about who actually downloaded. This hook
 * calls the dedicated endpoint instead, which issues a fresh presigned URL AND
 * writes a `download` row to the document access log.
 *
 * @returns Mutation that takes a document id and opens the returned URL
 */
export function useDocumentDownload() {
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (documentId: string) =>
      DocumentsService.getApiV1DocumentsDownload(documentId),
    onSuccess: (data) => {
      if (!data?.download_url) return;
      // A programmatic anchor click, NOT window.open: the presigned url only
      // arrives after the audited endpoint answers, so the open is no longer
      // inside the click's user gesture and Safari and Firefox block it as a
      // popup. The backend signs the url with Content-Disposition: attachment,
      // so activating the anchor downloads the file without navigating away.
      const anchor = document.createElement('a');
      anchor.href = data.download_url;
      anchor.rel = 'noopener noreferrer';
      // Ignored cross-origin; the signed Content-Disposition carries the name.
      anchor.download = data.filename ?? '';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    },
    onError: () => {
      toast.error(
        i18n._({ id: 'documents.download.error', message: 'Download failed' }),
      );
    },
  });
}
